import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Radio from '@mui/material/Radio';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { MutableRefObject, useEffect, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FormDecimalField from '@/components/Common/Form/FormDecimalField';
import {
  getWarehouseConfig,
  parseDecimal,
  LENGTH_FORMAT,
} from '@/helpers/unit';
import { BagType, DisplayRow } from './types';
import { calculateResult, ValidationResult } from './validation';

export interface FormValues {
  bagType: BagType | null;
  agentValues: (string | undefined)[];
}

interface MeasurementAssistantFormProps {
  displayRows: DisplayRow[];
  onResultChange: (result: ValidationResult | null, values: FormValues) => void;
  resetRef: MutableRefObject<(() => void) | null>;
  initialFormValues?: FormValues;
}

const MeasurementAssistantForm = ({
  displayRows,
  onResultChange,
  resetRef,
  initialFormValues,
}: MeasurementAssistantFormProps) => {
  const { t } = useTranslation();

  const config = getWarehouseConfig();
  const isInches = config?.lengthFormat === LENGTH_FORMAT.IN;
  const displayUnit = isInches ? LENGTH_FORMAT.IN : LENGTH_FORMAT.CM;

  const emptyDefaultsRef = useRef<FormValues>({
    bagType: null,
    agentValues: displayRows.map(() => undefined),
  });

  const { control, watch, setValue, reset } = useForm<FormValues>({
    defaultValues: initialFormValues ?? emptyDefaultsRef.current,
  });

  const bagType = watch('bagType');
  const agentValues = watch('agentValues');

  const allFilled =
    bagType !== null &&
    agentValues.length === displayRows.length &&
    agentValues.every(
      (v) => v !== undefined && v !== '' && parseDecimal(v) > 0
    );

  useEffect(() => {
    resetRef.current = () => reset(emptyDefaultsRef.current);
  }, [reset, resetRef]);

  const agentValuesKey = agentValues.join(',');

  const result = useMemo(() => {
    if (!allFilled || !bagType) return null;
    const numericAgentValues = agentValues.map((v) => parseDecimal(v ?? ''));
    const sellerValues = displayRows
      .filter((row) => row.sellerMeasurement !== undefined)
      .map((row) =>
        isInches
          ? row.sellerMeasurement!.valueIn
          : row.sellerMeasurement!.valueCm
      );
    return calculateResult(
      sellerValues,
      numericAgentValues,
      bagType,
      displayUnit
    );
  }, [bagType, agentValuesKey]); // agentValuesKey is a stable serialization of agentValues

  useEffect(() => {
    onResultChange(result, { bagType, agentValues });
  }, [result]);

  const bagTypeOptions: {
    value: BagType;
    labelKey: string;
    descKeys: string[];
  }[] = [
    {
      value: 'rigid',
      labelKey: 'QC_V2.MEASUREMENT.RIGID',
      descKeys: [
        'QC_V2.MEASUREMENT.RIGID_DESC_1',
        'QC_V2.MEASUREMENT.RIGID_DESC_2',
        'QC_V2.MEASUREMENT.RIGID_DESC_3',
        'QC_V2.MEASUREMENT.RIGID_DESC_4',
      ],
    },
    {
      value: 'soft',
      labelKey: 'QC_V2.MEASUREMENT.SOFT',
      descKeys: [
        'QC_V2.MEASUREMENT.SOFT_DESC_1',
        'QC_V2.MEASUREMENT.SOFT_DESC_2',
        'QC_V2.MEASUREMENT.SOFT_DESC_3',
      ],
    },
  ];

  return (
    <Box>
      {/* Section 1: Bag type */}
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5 }}>
        {t('QC_V2.MEASUREMENT.BAG_TYPE_QUESTION')}
      </Typography>

      <Controller
        name="bagType"
        control={control}
        render={({ field }) => (
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            {bagTypeOptions.map((option) => (
              <Box
                key={option.value}
                component="label"
                htmlFor={`bag-type-${option.value}`}
                sx={{
                  flex: 1,
                  border: '1px solid',
                  borderColor:
                    field.value === option.value ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  p: 1.5,
                  cursor: 'pointer',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  <Radio
                    id={`bag-type-${option.value}`}
                    size="small"
                    checked={field.value === option.value}
                    onChange={() => field.onChange(option.value)}
                    value={option.value}
                    sx={{ p: 0 }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t(option.labelKey)}
                  </Typography>
                </Box>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {option.descKeys.map((key) => (
                    <Typography
                      key={key}
                      component="li"
                      variant="caption"
                      sx={{ mb: 0.25, listStyleType: 'disc' }}
                    >
                      {t(key)}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      />

      {/* Section 2: Dimensions */}
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5 }}>
        {t('QC_V2.MEASUREMENT.MEASURE_QUESTION')}
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('QC_V2.MEASUREMENT.ELEMENT')}</TableCell>
            <TableCell>
              {t('QC_V2.MEASUREMENT.LISTING_VALUE')} (
              {displayUnit.toLowerCase()})
            </TableCell>
            <TableCell>
              {t('QC_V2.MEASUREMENT.ACTUAL_VALUE')} (
              {t('QC_V2.MEASUREMENT.GREATER_THAN')} 0{' '}
              {displayUnit.toLowerCase()})
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayRows.map((row, idx) => {
            const seller = row.sellerMeasurement;
            const sellerDisplayValue = seller
              ? isInches
                ? seller.valueIn
                : seller.valueCm
              : '-';

            return (
              <TableRow key={row.dimension.id}>
                <TableCell>{row.dimension.name}</TableCell>
                <TableCell>{sellerDisplayValue}</TableCell>
                <TableCell>
                  <Controller
                    name={`agentValues.${idx}`}
                    control={control}
                    render={({ field }) => (
                      <FormDecimalField
                        control={control}
                        name={`agentValues.${idx}`}
                        value={field.value ?? ''}
                        label=""
                        onChange={(value) => {
                          setValue(`agentValues.${idx}`, value || '');
                        }}
                      />
                    )}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Outcome row — always show both OK and KO, highlight the result */}
      {result && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2">
            {t('QC_V2.MEASUREMENT.OUTCOME')}
          </Typography>
          <Chip
            label={
              result.incorrectMeasurement
                ? t('QC_V2.MEASUREMENT.OVERALL_KO')
                : t('QC_V2.MEASUREMENT.OVERALL_OK')
            }
            color={result.incorrectMeasurement ? 'error' : 'success'}
          />
        </Box>
      )}
    </Box>
  );
};

export default MeasurementAssistantForm;
